import { STOCK_FIELD_STOCK_TAKE_DATE, STOCK_TAKE_NEVER_COUNTED_CUTOFF_MS } from './stock-take-stock-date.utils';
import { StockTakePopulationSortOrder } from '../models/stock-take-population-sort.enum';

/** Typesense field name for last stock take date (matches Firestore `_stock_take_date`). */
export const STOCK_TAKE_TYPESENSE_STOCK_TAKE_DATE_FIELD = STOCK_FIELD_STOCK_TAKE_DATE;

/** Sort oldest stock take date first when populating stock takes from Typesense. */
export const STOCK_TAKE_LAST_STOCK_TAKE_SORT_FIELD = `${STOCK_TAKE_TYPESENSE_STOCK_TAKE_DATE_FIELD}:asc`;

/** Typesense field for stock item description (`desc` → `1`). */
export const STOCK_TAKE_TYPESENSE_DESCRIPTION_FIELD = '1';

/** Sort description ascending when populating stock takes from Typesense. */
export const STOCK_TAKE_DESCRIPTION_SORT_FIELD = `${STOCK_TAKE_TYPESENSE_DESCRIPTION_FIELD}:asc`;

/**
 * Resolve Typesense `sort_by` for uncounted-item population.
 */
export function resolveStockTakePopulationSortField(
  sortOrder: StockTakePopulationSortOrder = StockTakePopulationSortOrder.oldestCountedDate,
): string {
  if (sortOrder === StockTakePopulationSortOrder.description) {
    return STOCK_TAKE_DESCRIPTION_SORT_FIELD;
  }
  return STOCK_TAKE_LAST_STOCK_TAKE_SORT_FIELD;
}

/**
 * Minimum epoch seconds for a valid indexed `_stock_take_date` (year 2000 UTC).
 * The Typesense field is int64 — use `>=` filters, not `is_empty` (invalid for numeric fields).
 */
export const TYPESENSE_VALID_STOCK_TAKE_DATE_FLOOR_SEC = Math.floor(STOCK_TAKE_NEVER_COUNTED_CUTOFF_MS / 1000);

/**
 * Typesense filter for stock items with a valid `_stock_take_date` in the search index.
 */
export function buildTypesenseValidStockTakeDateFilter(): string {
  return `${STOCK_TAKE_TYPESENSE_STOCK_TAKE_DATE_FIELD}:>=${TYPESENSE_VALID_STOCK_TAKE_DATE_FLOOR_SEC}`;
}

/**
 * @deprecated int64 fields do not support `is_empty` in Typesense. Missing counts use total minus valid.
 */
export const TYPESENSE_MISSING_STOCK_TAKE_DATE_FILTER = `$(${STOCK_TAKE_TYPESENSE_STOCK_TAKE_DATE_FIELD}(is_empty:true))`;

/**
 * True when the Typesense date check has not finished yet.
 */
export function isStockTakeTypesenseDateCheckPending(
  missingCount: number | null,
  schemaReady: boolean | null,
): boolean {
  return missingCount == null || schemaReady == null;
}

/**
 * True when every indexed stock item has a populated `_stock_take_date` in Typesense.
 * Never-counted items legitimately have no date — use {@link isTypesenseStockTakeDateSchemaReady} for gating.
 */
export function isTypesenseStockTakeDateFullyPopulated(
  missingCount: number | null,
  schemaReady: boolean | null,
): boolean {
  return schemaReady === true && missingCount === 0;
}

/** @deprecated Use {@link isTypesenseStockTakeDateFullyPopulated}. */
export const isTypesenseStockTakeDateReady = isTypesenseStockTakeDateFullyPopulated;

/**
 * True when the Typesense schema exposes `_stock_take_date` for search/sort.
 */
export function isTypesenseStockTakeDateSchemaReady(schemaReady: boolean | null): boolean {
  return schemaReady === true;
}

/**
 * True when stock take must be paused because `_stock_take_date` is absent from the Typesense schema.
 * A high missing-date count alone does not block — never-counted items are indexed with a null date.
 */
export function shouldShowTypesenseMissingDateAlert(
  _missingCount: number | null,
  schemaReady: boolean | null,
): boolean {
  return schemaReady === false;
}

/**
 * True when stock take must stay paused because the Typesense date field is not in the schema.
 */
export function isStockTakeBlockedByTypesenseDates(
  missingCount: number | null,
  schemaReady: boolean | null,
): boolean {
  if (isStockTakeTypesenseDateCheckPending(missingCount, schemaReady)) {
    return false;
  }
  return schemaReady === false;
}
